import { DestroyRef, inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  firstValueFrom,
  from,
  fromEvent,
  map,
  merge,
  mergeMap,
  Subject,
  timer,
  toArray,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IndexedDbService } from '@core/services/indexed-db.service';
import {
  PhotoQueueBlobRecord,
  PhotoQueueEntry,
  PhotoQueueError,
  PhotoQueueTask,
  PhotoQueueTarget,
  PhotoRemoveQueueTask,
  PhotoUploadQueueTask,
} from './input-photos-queue.types';
import {
  LegacyPhotoQueueTask,
  PhotoQueueEvent,
  PhotoQueueExecutionResult,
} from './photo-queue.types';
import { PhotoQueueExecutorService } from './photo-queue-executor.service';
import {
  PhotoQueueTaskExecutionError,
  isStalePhotoQueueTask,
  PHOTO_QUEUE_STALE_TARGET_CODE,
} from './photo-queue-error';
import { WorkflowStorageOwnerService } from './storage/workflow-storage-owner.service';
import type { WorkflowStorageOwner } from './storage/workflow-storage.types';

@Injectable({ providedIn: 'root' })
export class PhotoQueueService {
  private readonly indexedDbService = inject(IndexedDbService);
  private readonly executor = inject(PhotoQueueExecutorService);
  private readonly ownerService = inject(WorkflowStorageOwnerService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly taskKeyPrefix = 'photo_queue_task';
  private readonly blobKeyPrefix = 'photo_queue_blob';
  private readonly maxAutomaticAttempts = 5;
  private readonly successReceiptTtlMs = 24 * 60 * 60 * 1000;
  private readonly retryDelaysMs = [
    30_000, 60_000, 120_000, 240_000, 480_000,
  ] as const;
  private readonly eventSubject = new Subject<PhotoQueueEvent>();
  private readonly processingConcurrency = 3;

  readonly events$ = this.eventSubject.asObservable();

  private processorStarted = false;
  private processingPromise: Promise<void> | null = null;
  private rerunRequested = false;
  private rerunIgnoreBackoff = false;

  startProcessor(): void {
    if (this.processorStarted) {
      return;
    }

    this.processorStarted = true;

    merge(
      timer(0, 30_000).pipe(map(() => false)),
      fromEvent(window, 'online').pipe(map(() => true)),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ignoreBackoff => {
        void this.processQueue(ignoreBackoff).catch(error => {
          console.error('[PhotoQueueService] processQueue failed:', error);
        });
      });
  }

  async addUploadTask(
    task: PhotoUploadQueueTask,
    blobRecord: PhotoQueueBlobRecord,
  ): Promise<void> {
    const ownedTask: PhotoUploadQueueTask = {
      ...task,
      owner: this.ownerService.requireOwner(),
    };

    await this.indexedDbService.executeBatch({
      puts: [
        { key: this.getBlobKey(blobRecord.blobKey), value: blobRecord },
        { key: this.getTaskKey(ownedTask.id), value: ownedTask },
      ],
    });

    void this.processQueue().catch(error => {
      console.error(
        '[PhotoQueueService] queued upload processing failed:',
        error,
      );
    });
  }

  async addRemoveTask(task: PhotoRemoveQueueTask): Promise<void> {
    const ownedTask: PhotoRemoveQueueTask = {
      ...task,
      owner: this.ownerService.requireOwner(),
    };

    await this.indexedDbService.setItem(
      this.getTaskKey(ownedTask.id),
      ownedTask,
    );

    void this.processQueue().catch(error => {
      console.error(
        '[PhotoQueueService] queued remove processing failed:',
        error,
      );
    });
  }

  /**
   * Задачи текущего владельца (окружение + пользователь)
   */
  async getAll(): Promise<PhotoQueueTask[]> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return [];
    }

    const tasks = await this.getAllStored();

    return tasks.filter(task => isOwnedBy(task, owner));
  }

  /**
   * Количество неотправленных задач текущего владельца (без квитанций об успехе)
   */
  async getPendingCount(): Promise<number> {
    const tasks = await this.getAll();

    return tasks.filter(
      task => task.state !== 'succeeded' && !isStalePhotoQueueTask(task),
    ).length;
  }

  /**
   * Пометить задачу, цель которой исчезла из схемы регламента, как конфликт без повторов
   */
  async markStaleTarget(taskId: string): Promise<void> {
    const task = await this.getTask(taskId);

    if (!task || task.state === 'succeeded' || isStalePhotoQueueTask(task)) {
      return;
    }

    const error: PhotoQueueError = {
      code: PHOTO_QUEUE_STALE_TARGET_CODE,
      message: 'Workflow form or field of the task no longer exists',
      retryable: false,
      occurredAt: Date.now(),
    };

    await this.indexedDbService.setItem(this.getTaskKey(task.id), {
      ...task,
      state: 'failed',
      nextAttemptAt: 0,
      lastError: error,
    });
    this.eventSubject.next({ ...this.createBaseEvent(task, 'failed'), error });
  }

  /**
   * Удалить задачи без владельца (созданные до изоляции очередей): их автора установить нельзя.
   * Возвращает количество удалённых задач.
   */
  async removeUnownedTasks(): Promise<number> {
    const unowned = (await this.getAllStored()).filter(task => !task.owner);

    for (const task of unowned) {
      await this.deleteTaskAndBlob(task);
    }

    return unowned.length;
  }

  private async getAllStored(): Promise<PhotoQueueTask[]> {
    const entries = await this.indexedDbService.getAllEntries<unknown>();
    const taskEntries = entries.filter(entry => this.isTaskKey(entry.key));
    const tasks = await Promise.all(
      taskEntries.map(async ({ key, value }) => {
        const task = this.normalizeTask(value, key);

        if (!this.isVersionTwoTask(value)) {
          await this.indexedDbService.setItem(this.getTaskKey(task.id), task);

          if (key !== this.getTaskKey(task.id)) {
            await this.indexedDbService.removeItem(key);
          }
        }

        return task;
      }),
    );

    return tasks.sort((first, second) => first.createdAt - second.createdAt);
  }

  async getEntries(): Promise<PhotoQueueEntry[]> {
    const tasks = await this.getAll();

    return Promise.all(
      tasks.map(async task => ({
        task,
        blobRecord:
          task.operation === 'upload' ? await this.getBlob(task.blobKey) : null,
      })),
    );
  }

  async getBlob(blobKey: string): Promise<PhotoQueueBlobRecord | null> {
    return this.indexedDbService.getItem<PhotoQueueBlobRecord>(
      this.getBlobKey(blobKey),
    );
  }

  async cancel(taskId: string): Promise<void> {
    const task = await this.getTask(taskId);

    if (!task) {
      return;
    }

    await this.deleteTaskAndBlob(task);
    this.eventSubject.next(this.createBaseEvent(task, 'cancelled'));
  }

  async retry(taskId: string): Promise<void> {
    const task = await this.getTask(taskId);

    if (!task || task.state !== 'failed' || isStalePhotoQueueTask(task)) {
      return;
    }

    const pendingTask: PhotoQueueTask = {
      ...task,
      state: 'pending',
      attemptCount: 0,
      nextAttemptAt: Date.now(),
      lastError: undefined,
      completedAt: undefined,
    };

    await this.indexedDbService.setItem(this.getTaskKey(task.id), pendingTask);
    await this.processQueue(true);
  }

  async acknowledgeSuccess(taskId: string): Promise<void> {
    const task = await this.getTask(taskId);

    if (!task || task.state !== 'succeeded') {
      return;
    }

    await this.deleteTaskAndBlob(task);
  }

  processQueue(ignoreBackoff = false): Promise<void> {
    if (this.processingPromise) {
      this.rerunRequested = true;
      this.rerunIgnoreBackoff = this.rerunIgnoreBackoff || ignoreBackoff;

      return this.processingPromise;
    }

    const processingPromise = this.runProcessQueue(ignoreBackoff).finally(
      () => {
        if (this.processingPromise === processingPromise) {
          this.processingPromise = null;
        }

        // Задача могла быть добавлена/отменена/повторена, пока шла эта
        // обработка — не ждём следующего тика таймера/online, а сразу
        // запускаем ещё один проход.
        if (this.rerunRequested) {
          const rerunIgnoreBackoff = this.rerunIgnoreBackoff;

          this.rerunRequested = false;
          this.rerunIgnoreBackoff = false;

          void this.processQueue(rerunIgnoreBackoff).catch(error => {
            console.error('[PhotoQueueService] queued rerun failed:', error);
          });
        }
      },
    );

    this.processingPromise = processingPromise;

    return processingPromise;
  }

  private async runProcessQueue(ignoreBackoff: boolean): Promise<void> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    const tasks = await this.getAll();
    const now = Date.now();
    const tasksToRun: PhotoQueueTask[] = [];

    for (const task of tasks) {
      if (task.state === 'succeeded') {
        if (
          task.completedAt !== undefined &&
          now - task.completedAt >= this.successReceiptTtlMs
        ) {
          await this.deleteTaskAndBlob(task);
        }

        continue;
      }

      if (task.state === 'failed') {
        continue;
      }

      // Ожидающие повтора задачи тоже попадают в партицию: там они блокируют
      // более поздние операции того же поля до своего выполнения
      tasksToRun.push(task);
    }

    if (tasksToRun.length === 0) {
      return;
    }

    // Операции одного поля (загрузка и удаление фото) выполняются строго по порядку
    // создания, иначе удаление может обогнать загрузку. Разные поля — параллельно
    // с ограничением, чтобы очередь из нескольких полей не выгружалась последовательно.
    const partitions = new Map<string, PhotoQueueTask[]>();

    for (const task of tasksToRun) {
      const key = getPhotoTargetKey(task);

      partitions.set(key, [...(partitions.get(key) ?? []), task]);
    }

    await firstValueFrom(
      from([...partitions.values()]).pipe(
        mergeMap(
          partitionTasks => this.runPartition(partitionTasks, ignoreBackoff),
          this.processingConcurrency,
        ),
        toArray(),
      ),
    );
  }

  private async runPartition(
    tasks: PhotoQueueTask[],
    ignoreBackoff: boolean,
  ): Promise<void> {
    for (const task of tasks) {
      // Ранняя операция поля ждёт повтора — более поздние операции этого поля ждут её
      if (!ignoreBackoff && task.nextAttemptAt > Date.now()) {
        return;
      }

      await this.runTask(task);

      const updated = await this.getTask(task.id);

      // Задача не завершилась (ждёт повтора/упала) — следующие операции этого поля ждут,
      // чтобы не нарушить порядок загрузка → удаление
      if (updated && updated.state !== 'succeeded') {
        return;
      }
    }
  }

  private async runTask(task: PhotoQueueTask): Promise<void> {
    const owner = this.ownerService.getOwner();

    // Сессия могла смениться после чтения очереди: чужую задачу не отправляем
    if (!owner || !isOwnedBy(task, owner)) {
      return;
    }

    const blobRecord =
      task.operation === 'upload' ? await this.getBlob(task.blobKey) : null;

    this.eventSubject.next(this.createBaseEvent(task, 'started'));

    let result: PhotoQueueExecutionResult;

    try {
      result = await this.executor.execute(task, blobRecord);
    } catch (error) {
      await this.handleExecutionError(task, error);

      return;
    }

    const succeededTask = this.createSucceededTask(task, result);

    await this.indexedDbService.setItem(
      this.getTaskKey(task.id),
      succeededTask,
    );
    this.eventSubject.next({
      ...this.createBaseEvent(task, 'succeeded'),
      result,
    });
  }

  private async handleExecutionError(
    task: PhotoQueueTask,
    error: unknown,
  ): Promise<void> {
    const queueError = this.toQueueError(error);
    const attemptCount = task.attemptCount + 1;

    if (!queueError.retryable || attemptCount >= this.maxAutomaticAttempts) {
      const failedTask: PhotoQueueTask = {
        ...task,
        state: 'failed',
        attemptCount,
        nextAttemptAt: 0,
        lastError: queueError,
      };

      await this.indexedDbService.setItem(this.getTaskKey(task.id), failedTask);
      this.eventSubject.next({
        ...this.createBaseEvent(task, 'failed'),
        error: queueError,
      });

      return;
    }

    const nextAttemptAt = Date.now() + this.getRetryDelayMs(attemptCount);
    const retryTask: PhotoQueueTask = {
      ...task,
      state: 'retry_wait',
      attemptCount,
      nextAttemptAt,
      lastError: queueError,
    };

    await this.indexedDbService.setItem(this.getTaskKey(task.id), retryTask);
    this.eventSubject.next({
      ...this.createBaseEvent(task, 'retry_scheduled'),
      error: queueError,
      nextAttemptAt,
    });
  }

  private createSucceededTask(
    task: PhotoQueueTask,
    result: PhotoQueueExecutionResult,
  ): PhotoQueueTask {
    const completedAt = Date.now();

    if (task.operation === 'upload') {
      if (result.operation !== 'upload') {
        throw new PhotoQueueTaskExecutionError(
          'executor_result_mismatch',
          'Upload executor returned an invalid result',
          false,
        );
      }

      return {
        ...task,
        state: 'succeeded',
        completedAt,
        nextAttemptAt: 0,
        lastError: undefined,
        result: result.result,
      };
    }

    if (result.operation !== 'remove') {
      throw new PhotoQueueTaskExecutionError(
        'executor_result_mismatch',
        'Remove executor returned an invalid result',
        false,
      );
    }

    return {
      ...task,
      state: 'succeeded',
      completedAt,
      nextAttemptAt: 0,
      lastError: undefined,
    };
  }

  private toQueueError(error: unknown): PhotoQueueError {
    if (error instanceof PhotoQueueTaskExecutionError) {
      return {
        code: error.code,
        message: error.message,
        retryable: error.retryable,
        occurredAt: Date.now(),
        httpStatus: error.httpStatus,
      };
    }

    if (error instanceof HttpErrorResponse) {
      return {
        code: `http_${error.status}`,
        message: error.message || 'Photo synchronization request failed',
        retryable: this.isRetryableHttpStatus(error.status),
        occurredAt: Date.now(),
        httpStatus: error.status,
      };
    }

    return {
      code: 'unknown_execution_error',
      message: error instanceof Error ? error.message : 'Unknown error',
      retryable: true,
      occurredAt: Date.now(),
    };
  }

  private isRetryableHttpStatus(status: number): boolean {
    return (
      status === 0 ||
      status === 408 ||
      status === 425 ||
      status === 429 ||
      status >= 500
    );
  }

  private getRetryDelayMs(attemptIndex: number): number {
    const baseDelay =
      this.retryDelaysMs[
        Math.min(attemptIndex - 1, this.retryDelaysMs.length - 1)
      ];

    return Math.round(baseDelay);
  }

  private async getTask(taskId: string): Promise<PhotoQueueTask | null> {
    const storedValue = await this.indexedDbService.getItem<unknown>(
      this.getTaskKey(taskId),
    );

    if (storedValue === null) {
      return null;
    }

    const task = this.normalizeTask(storedValue, this.getTaskKey(taskId));
    const owner = this.ownerService.getOwner();

    // Чужие задачи недоступны для отмены/повтора
    return owner && isOwnedBy(task, owner) ? task : null;
  }

  private async deleteTaskAndBlob(task: PhotoQueueTask): Promise<void> {
    await this.indexedDbService.executeBatch({
      deletes:
        task.operation === 'upload'
          ? [this.getTaskKey(task.id), this.getBlobKey(task.blobKey)]
          : [this.getTaskKey(task.id)],
    });
  }

  private createBaseEvent<TType extends PhotoQueueEvent['type']>(
    task: PhotoQueueTask,
    type: TType,
  ): {
    readonly type: TType;
    readonly taskId: string;
    readonly photoId: string;
    readonly operation: PhotoQueueTask['operation'];
  } {
    return {
      type,
      taskId: task.id,
      photoId: task.photoId,
      operation: task.operation,
    };
  }

  private normalizeTask(value: unknown, storageKey: string): PhotoQueueTask {
    if (this.isVersionTwoTask(value)) {
      return value;
    }

    if (this.isRecord(value) && value['schemaVersion'] === 2) {
      return this.createInvalidVersionTwoTask(value, storageKey);
    }

    const legacy = this.asLegacyTask(value);
    const id =
      typeof legacy.id === 'string'
        ? legacy.id
        : storageKey.slice(`${this.taskKeyPrefix}:`.length) ||
          crypto.randomUUID();
    const target = this.normalizeTarget(legacy.target);
    const createdAt =
      typeof legacy.createdAt === 'number' ? legacy.createdAt : Date.now();
    const attemptCount =
      typeof legacy.retryCount === 'number' ? legacy.retryCount : 0;
    const legacyError =
      typeof legacy.errorMessage === 'string'
        ? this.createMigrationError('legacy_error', legacy.errorMessage, true)
        : undefined;

    if (legacy.operation === 'remove') {
      const createdSubmissionFileId =
        typeof legacy.createdSubmissionFileId === 'number'
          ? legacy.createdSubmissionFileId
          : 0;
      const isValid = createdSubmissionFileId > 0 && target !== null;

      return {
        schemaVersion: 2,
        id,
        photoId: `server:${createdSubmissionFileId || id}`,
        operation: 'remove',
        target: target ?? this.createInvalidTarget(),
        createdAt,
        attemptCount,
        state: isValid ? 'pending' : 'failed',
        nextAttemptAt: isValid ? Date.now() : 0,
        createdSubmissionFileId,
        lastError: isValid
          ? legacyError
          : this.createMigrationError(
              'invalid_legacy_remove_task',
              'Legacy remove task is missing required data',
              false,
            ),
      };
    }

    const blobKey =
      typeof legacy.blobKey === 'string' && legacy.blobKey.length > 0
        ? legacy.blobKey
        : `missing:${id}`;
    const isValid = legacy.operation === 'upload' && target !== null;

    return {
      schemaVersion: 2,
      id,
      photoId: blobKey,
      operation: 'upload',
      target: target ?? this.createInvalidTarget(),
      createdAt,
      attemptCount,
      state: isValid ? 'pending' : 'failed',
      nextAttemptAt: isValid ? Date.now() : 0,
      blobKey,
      lastError: isValid
        ? legacyError
        : this.createMigrationError(
            'invalid_legacy_upload_task',
            'Legacy upload task is missing required data',
            false,
          ),
    };
  }

  private isVersionTwoTask(value: unknown): value is PhotoQueueTask {
    if (!this.isRecord(value) || value['schemaVersion'] !== 2) {
      return false;
    }

    const operation = value['operation'];
    const state = value['state'];

    return (
      (operation === 'upload' || operation === 'remove') &&
      (state === 'pending' ||
        state === 'retry_wait' ||
        state === 'failed' ||
        state === 'succeeded') &&
      typeof value['id'] === 'string' &&
      typeof value['photoId'] === 'string' &&
      typeof value['createdAt'] === 'number' &&
      typeof value['attemptCount'] === 'number' &&
      typeof value['nextAttemptAt'] === 'number' &&
      this.normalizeTarget(value['target']) !== null &&
      (operation === 'upload'
        ? typeof value['blobKey'] === 'string'
        : typeof value['createdSubmissionFileId'] === 'number')
    );
  }

  private createInvalidVersionTwoTask(
    value: Record<string, unknown>,
    storageKey: string,
  ): PhotoQueueTask {
    const id =
      typeof value['id'] === 'string'
        ? value['id']
        : storageKey.slice(`${this.taskKeyPrefix}:`.length) ||
          crypto.randomUUID();
    const createdAt =
      typeof value['createdAt'] === 'number' ? value['createdAt'] : Date.now();
    const target =
      this.normalizeTarget(value['target']) ?? this.createInvalidTarget();
    const error = this.createMigrationError(
      'invalid_photo_queue_task',
      'Photo queue task with schema version 2 is missing required data',
      false,
    );
    const owner = this.normalizeOwner(value['owner']);

    if (value['operation'] === 'remove') {
      const createdSubmissionFileId =
        typeof value['createdSubmissionFileId'] === 'number'
          ? value['createdSubmissionFileId']
          : 0;

      return {
        schemaVersion: 2,
        id,
        photoId:
          typeof value['photoId'] === 'string'
            ? value['photoId']
            : `server:${createdSubmissionFileId || id}`,
        operation: 'remove',
        target,
        createdAt,
        attemptCount:
          typeof value['attemptCount'] === 'number' ? value['attemptCount'] : 0,
        state: 'failed',
        nextAttemptAt: 0,
        createdSubmissionFileId,
        lastError: error,
        ...(owner ? { owner } : {}),
      };
    }

    const blobKey =
      typeof value['blobKey'] === 'string' ? value['blobKey'] : `missing:${id}`;

    return {
      schemaVersion: 2,
      id,
      photoId:
        typeof value['photoId'] === 'string' ? value['photoId'] : blobKey,
      operation: 'upload',
      target,
      createdAt,
      attemptCount:
        typeof value['attemptCount'] === 'number' ? value['attemptCount'] : 0,
      state: 'failed',
      nextAttemptAt: 0,
      blobKey,
      lastError: error,
      ...(owner ? { owner } : {}),
    };
  }

  private normalizeOwner(value: unknown): WorkflowStorageOwner | undefined {
    if (
      this.isRecord(value) &&
      typeof value['environment'] === 'string' &&
      typeof value['userId'] === 'number'
    ) {
      return { environment: value['environment'], userId: value['userId'] };
    }

    return undefined;
  }

  private asLegacyTask(value: unknown): LegacyPhotoQueueTask {
    return this.isRecord(value) ? value : {};
  }

  private normalizeTarget(value: unknown): PhotoQueueTarget | null {
    if (
      !this.isRecord(value) ||
      typeof value['requestNumber'] !== 'string' ||
      typeof value['workflowId'] !== 'string' ||
      typeof value['nodeId'] !== 'number' ||
      typeof value['formId'] !== 'number' ||
      typeof value['fieldId'] !== 'string'
    ) {
      return null;
    }

    return {
      requestNumber: value['requestNumber'],
      workflowId: value['workflowId'],
      nodeId: value['nodeId'],
      formId: value['formId'],
      fieldId: value['fieldId'],
    };
  }

  private createInvalidTarget(): PhotoQueueTarget {
    return {
      requestNumber: '',
      workflowId: '',
      nodeId: 0,
      formId: 0,
      fieldId: '',
    };
  }

  private createMigrationError(
    code: string,
    message: string,
    retryable: boolean,
  ): PhotoQueueError {
    return {
      code,
      message,
      retryable,
      occurredAt: Date.now(),
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private getBlobKey(blobKey: string): string {
    return `${this.blobKeyPrefix}:${blobKey}`;
  }

  private getTaskKey(taskId: string): string {
    return `${this.taskKeyPrefix}:${taskId}`;
  }

  private isTaskKey(key: string): boolean {
    return key.startsWith(`${this.taskKeyPrefix}:`);
  }
}

function isOwnedBy(task: PhotoQueueTask, owner: WorkflowStorageOwner): boolean {
  return (
    task.owner?.environment === owner.environment &&
    task.owner?.userId === owner.userId
  );
}

/**
 * Партиция фото-задач: конкретное поле экземпляра формы
 */
function getPhotoTargetKey(task: PhotoQueueTask): string {
  return getPhotoQueueTargetKey(task.target);
}

/**
 * Ключ поля экземпляра формы: (requestNumber, requestWorkflowId, nodeId, formId, fieldKey)
 */
export function getPhotoQueueTargetKey(target: PhotoQueueTarget): string {
  const { requestNumber, workflowId, nodeId, formId, fieldId } = target;

  return [requestNumber, workflowId, nodeId, formId, fieldId].join(':');
}
